import {Euler} from 'three/src/math/Euler';
import {EventDispatcher} from 'three/src/core/EventDispatcher';
import {Vector3} from 'three/src/math/Vector3';

var PointerLockControls = function (camera, domElement) {
	if (domElement === undefined) {
		console.warn('THREE.PointerLockControls: The second parameter "domElement" is now mandatory.');
		domElement = document.body;
	}

	this.domElement = domElement;
	this.isLocked = false;

	// Set to constrain the pitch of the camera
	// Range is 0 to Math.PI radians
	this.minPolarAngle = 0; // radians
	this.maxPolarAngle = Math.PI; // radians
	this.speed = 1;

	//
	// internals
	//

	var scope = this;

	var changeEvent = {type: 'change'};
	var lockEvent = {type: 'lock'};
	var unlockEvent = {type: 'unlock'};

	var euler = new Euler(0, 0, 0, 'YXZ');

	var PI_2 = Math.PI / 2;

	var vec = new Vector3();

	function onMouseMove(event) {
		if (scope.isLocked === false) return;

		var movementX = event.movementX || event.mozMovementX || event.webkitMovementX || 0;
		var movementY = event.movementY || event.mozMovementY || event.webkitMovementY || 0;

		euler.setFromQuaternion(camera.quaternion);

		euler.y -= movementX * 0.002;
		euler.x -= movementY * 0.002;

		euler.x = Math.max(PI_2 - scope.maxPolarAngle, Math.min(PI_2 - scope.minPolarAngle, euler.x));

		camera.quaternion.setFromEuler(euler);

		scope.dispatchEvent(changeEvent);
	}

	function onPointerlockChange() {
		if (scope.domElement.ownerDocument.pointerLockElement === scope.domElement) {
			scope.dispatchEvent(lockEvent);

			scope.isLocked = true;
		} else {
			scope.dispatchEvent(unlockEvent);

			scope.isLocked = false;
		}
	}

	function onPointerlockError() {
		console.error('THREE.PointerLockControls: Unable to use Pointer Lock API');
	}

	this.connect = function () {
		scope.domElement.ownerDocument.addEventListener('mousemove', onMouseMove);
		scope.domElement.ownerDocument.addEventListener('pointerlockchange', onPointerlockChange);
		scope.domElement.ownerDocument.addEventListener('pointerlockerror', onPointerlockError);
	};

	this.disconnect = function () {
		scope.domElement.ownerDocument.removeEventListener('mousemove', onMouseMove);
		scope.domElement.ownerDocument.removeEventListener('pointerlockchange', onPointerlockChange);
		scope.domElement.ownerDocument.removeEventListener('pointerlockerror', onPointerlockError);
	};

	this.dispose = function () {
		this.disconnect();
	};

	this.getObject = function () {
		// retaining this method for backward compatibility

		return camera;
	};

	this.getDirection = (function () {
		var direction = new Vector3(0, 0, -1);

		return function (v) {
			return v.copy(direction).applyQuaternion(camera.quaternion);
		};
	})();

	this.moveForward = function (distance) {
		// move forward parallel to the xz-plane
		// assumes camera.up is y-up

		vec.setFromMatrixColumn(camera.matrix, 0);

		vec.crossVectors(camera.up, vec);

		camera.position.addScaledVector(vec, distance);
	};

	this.moveRight = function (distance) {
		vec.setFromMatrixColumn(camera.matrix, 0);

		camera.position.addScaledVector(vec, distance);
	};

	this.lock = function () {
		this.domElement.requestPointerLock();
	};

	this.unlock = function () {
		scope.domElement.ownerDocument.exitPointerLock();
	};

	let velocity = new Vector3();
	let direction = new Vector3();

	let moveForward = false;
	let moveBackward = false;
	let moveLeft = false;
	let moveRight = false;
	this.setMoveForward = function (state) {
		moveForward = state;
	};
	this.setMoveBackward = function (state) {
		moveBackward = state;
	};
	this.setMoveLeft = function (state) {
		moveLeft = state;
	};
	this.setMoveRight = function (state) {
		moveRight = state;
	};
	let moveForwardDistance = 0;
	let moveRightDistance = 0;
	let prevTime = 0;
	this.update = function () {
		const time = performance.now();

		if (this.isLocked === true) {
			const delta = (time - prevTime) / 1000;
			velocity.x -= velocity.x * 10.0 * delta;
			velocity.z -= velocity.z * 10.0 * delta;

			velocity.y -= 9.8 * 100.0 * delta; // 100.0 = mass

			direction.z = Number(moveForward) - Number(moveBackward);
			direction.x = Number(moveRight) - Number(moveLeft);
			direction.normalize(); // this ensures consistent movements in all directions

			if (moveForward || moveBackward) velocity.z -= direction.z * 400.0 * delta * this.speed;
			if (moveLeft || moveRight) velocity.x -= direction.x * 400.0 * delta * this.speed;

			this.moveRight(-velocity.x * delta);
			this.moveForward(-velocity.z * delta);
		}
		prevTime = time;
	};

	this.connect();
};

PointerLockControls.prototype = Object.create(EventDispatcher.prototype);
PointerLockControls.prototype.constructor = PointerLockControls;

export {PointerLockControls};
